// Local file-bytes store for uploaded documents.
//
// Document *metadata* lives with the rest of the mock state in localStorage,
// but the bytes are far too large for that (5 MB quota, string-encoded). They
// go in IndexedDB instead, keyed by document id, so a document uploaded today
// still opens and downloads after a reload. When VITE_FAMILY_API_URL is set
// the bytes go to the backend and this store is bypassed entirely.

const DB_NAME = 'nb-family-documents'
const DB_VERSION = 1
const STORE = 'blobs'

/**
 * Upload ceiling.
 *
 * Set by the narrowest link in the chain, which is the API Gateway REST
 * payload limit of 10 MB — and the multipart body reaches it base64-encoded,
 * about a third larger than the file. 6 MB leaves room for that expansion and
 * the metadata part. Refusing an 8 MB file here with a clear message beats
 * letting the gateway reject it with an opaque one.
 *
 * Lifting this means moving uploads to a presigned S3 PUT, which bypasses the
 * gateway body entirely.
 */
export const MAX_FILE_BYTES = 6 * 1024 * 1024

let _dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'))
      return
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
  })
  // A failed open shouldn't poison every later call.
  _dbPromise.catch(() => {
    _dbPromise = null
  })
  return _dbPromise
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode)
        const request = run(transaction.objectStore(STORE))
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
      }),
  )
}

export async function putBlob(id: string, file: Blob): Promise<boolean> {
  try {
    await tx('readwrite', (store) => store.put(file, id))
    return true
  } catch (err) {
    console.warn('Document bytes could not be stored locally; metadata only.', err)
    return false
  }
}

export async function getBlob(id: string): Promise<Blob | null> {
  try {
    const blob = await tx<Blob | undefined>('readonly', (store) => store.get(id))
    return blob ?? null
  } catch {
    return null
  }
}

export async function deleteBlob(id: string): Promise<void> {
  try {
    await tx('readwrite', (store) => store.delete(id))
  } catch {
    // nothing stored — deleting metadata is enough
  }
}

// Object URLs are revoked on the next request so a long session doesn't leak
// one URL per preview click.
let _lastObjectUrl: string | null = null

export async function getBlobUrl(id: string): Promise<string | null> {
  const blob = await getBlob(id)
  if (!blob) return null
  if (_lastObjectUrl) URL.revokeObjectURL(_lastObjectUrl)
  _lastObjectUrl = URL.createObjectURL(blob)
  return _lastObjectUrl
}
